import Head from "next/head";
import { useRouter } from "next/router";

import { useMemo } from "react";

import { twMerge } from "tailwind-merge";

import CustomCard from "components/CustomCard";
import { entityUrl } from "components/EntityPageLayout";
import { Tab, Tabs } from "components/Tabs";
import { useApi } from "hooks/useApi";
import shortSecurity from "utils/shortSecurity";

import EntityPageInsightsList from "./EntityPageInsightsList";
import styles from "./style.module.scss";

type EntityPageAnalysisProps = {};

const EntityPageAnalysis = ({}: EntityPageAnalysisProps) => {
  const router = useRouter();
  const slug = router.query.slug as string;
  const currentTab = router.query.tab;

  const { data: entity } = useApi(entityUrl(slug));
  const { prettyName: name, security } = entity || {};
  const shortedSecurity = shortSecurity(security);
  const pageTitleName = useMemo(() => {
    if (shortedSecurity) {
      return `${name} | ${shortedSecurity} `;
    }
    return name;
  }, [shortedSecurity, name]);
  const title = `${pageTitleName} - Research, Discussions, Announcements, Analytics, SmartScore & News`;

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta property="og:title" content={title} />
      </Head>
      <Tabs className="pt-0">
        <Tab href={`/entities/${slug}/research`} active={!currentTab}>
          All
        </Tab>
        <Tab
          href={`/entities/${slug}/research?tab=insights`}
          active={currentTab === "insights"}
          tooltip="Independent research published by verified providers"
        >
          Insights
        </Tab>
        <Tab
          href={`/entities/${slug}/research?tab=broker-reports`}
          active={currentTab === "broker-reports"}
          tooltip="Research published by sell-side firms"
        >
          Broker
        </Tab>
        <Tab
          href={`/entities/${slug}/research?tab=sponsored`}
          active={currentTab === "sponsored"}
          tooltip="Research sponsored by issuers/companies or public/private market exchanges"
        >
          Sponsored
        </Tab>
        <Tab
          href={`/entities/${slug}/research?tab=syndicated`}
          active={currentTab === "syndicated"}
          tooltip="Research curated from across the web"
        >
          Syndicated
        </Tab>
        <Tab
          href={`/entities/${slug}/research?tab=podcast`}
          active={currentTab === "podcast"}
          tooltip="Audio curated from across the web"
        >
          Podcast
        </Tab>
        <Tab
          href={`/entities/${slug}/research?tab=buyside`}
          active={currentTab === "buyside"}
          tooltip="Research/letters published by the buy-side"
        >
          Buy side
        </Tab>
      </Tabs>
      <CustomCard
        className={twMerge(
          styles.card,
          "mb-0 md:mb-[30px]",
          "min-h-[max(calc(100vh-500px),720px)]"
        )}
      >
        <EntityPageInsightsList />
      </CustomCard>
    </>
  );
};

export default EntityPageAnalysis;
